<template>
  <div
    class="vc-section vc-card vc-card--compact vc-interrupt"
    :class="{ 'vc-interrupt--collapsed': !settings || !settings.enabled }"
  >
    <div class="vc-interrupt__head">
      <span class="vc-interrupt__title">打断设置</span>
      <div v-if="settings" class="vc-interrupt__switch">
        <span class="vc-interrupt__switch-label">{{ settings.enabled ? '开启' : '关闭' }}</span>
        <i-switch v-model="settings.enabled" />
      </div>
    </div>

    <div v-show="settings && settings.enabled" class="vc-interrupt__body">
      <div class="vc-interrupt-field">
        <div class="vc-interrupt-field__label">
          语气词过滤
          <Tooltip content="命中这些词不允许打断。" placement="top" :max-width="280" transfer>
            <Icon type="ios-help-circle-outline" class="vc-interrupt-help" />
          </Tooltip>
        </div>
        <KeywordTagEditor
          :value="settings.filler_words"
          placeholder="回车添加，例如：嗯、好的、对"
          @input="onListInput('filler_words', $event)"
        />
      </div>

      <div class="vc-interrupt-protect">
        <div class="vc-interrupt-protect__title">
          AI语句保护
          <Tooltip
            content="包含保护词的 AI 语句不支持打断。"
            placement="top"
            :max-width="280"
            transfer
          >
            <Icon type="ios-help-circle-outline" class="vc-interrupt-help" />
          </Tooltip>
        </div>
        <div class="vc-interrupt-protect__box">
          <div class="vc-interrupt-field vc-interrupt-field--nested">
            <div class="vc-interrupt-field__label vc-interrupt-field__label--sub">保护词</div>
            <KeywordTagEditor
              :value="settings.lock_words"
              placeholder="回车添加，例如：价格、手机号、验证码"
              @input="onListInput('lock_words', $event)"
            />
          </div>

          <div class="vc-interrupt-force-bar">
            <div v-show="forceRuleOpen" class="vc-interrupt-field vc-interrupt-field--nested">
              <div class="vc-interrupt-field__label vc-interrupt-field__label--sub">
                例外：以下词仍可打断
                <Tooltip
                  content="客户说了指定关键词仍可打断被锁定的语句。"
                  placement="top"
                  :max-width="280"
                  transfer
                >
                  <Icon type="ios-help-circle-outline" class="vc-interrupt-help" />
                </Tooltip>
              </div>
              <KeywordTagEditor
                :value="settings.force_interrupt_words"
                placeholder="回车添加，例如：等一下、我不需要"
                @input="onListInput('force_interrupt_words', $event)"
              />
            </div>
            <button
              v-if="!forceRuleOpen"
              type="button"
              class="vc-interrupt-force-add"
              @click="openForceRule"
            >
              + 添加强制打断规则
            </button>
            <button v-else type="button" class="vc-interrupt-force-collapse" @click="closeForceRule">
              <Icon type="ios-arrow-back" />
              <span>收起强制打断规则</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import KeywordTagEditor from '../shared/KeywordTagEditor.vue'
import {
  createDefaultInterruptSettings,
  ensureInterruptSettings,
} from '../../utils/callControlHelpers'

export default {
  name: 'InterruptSettingsSection',
  components: { KeywordTagEditor },
  props: {
    callControl: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      forceRuleOpen: false,
    }
  },
  computed: {
    settings() {
      return this.callControl.interrupt_settings
    },
  },
  mounted() {
    this.ensureShape()
  },
  methods: {
    ensureShape() {
      if (!this.callControl.interrupt_settings || typeof this.callControl.interrupt_settings !== 'object') {
        this.$set(this.callControl, 'interrupt_settings', createDefaultInterruptSettings())
      }
      ensureInterruptSettings(this.callControl)
      const s = this.callControl.interrupt_settings
      this.$set(s, 'utterance_lock_enabled', true)
      this.$set(s, 'force_interrupt_enabled', s.force_interrupt_enabled === true)
      this.$set(
        s,
        'force_interrupt_words',
        Array.isArray(s.force_interrupt_words) ? [...s.force_interrupt_words] : [],
      )
      this.forceRuleOpen = s.force_interrupt_enabled === true || s.force_interrupt_words.length > 0
    },
    onListInput(key, list) {
      if (!this.settings) return
      this.$set(this.settings, key, Array.isArray(list) ? [...list] : [])
    },
    openForceRule() {
      if (!this.settings) return
      this.forceRuleOpen = true
      this.$set(this.settings, 'force_interrupt_enabled', true)
    },
    closeForceRule() {
      this.forceRuleOpen = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import './shared.scss';

.vc-section {
  margin-bottom: 16px;
}

.vc-card {
  background: #fff;
  border: 1px solid #e8eaec;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: 20px;
}

.vc-card--compact {
  padding-bottom: 16px;
}

.vc-interrupt--collapsed {
  padding-bottom: 16px;

  .vc-interrupt__head {
    margin-bottom: 0;
  }
}

.vc-interrupt__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 24px;
  margin-bottom: 16px;
}

.vc-interrupt__title {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  line-height: 24px;
}

.vc-interrupt__switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  line-height: 24px;
}

.vc-interrupt__switch-label {
  font-size: 13px;
  color: #595959;
  line-height: 24px;
}

.vc-interrupt__body {
  text-align: left;
}

.vc-interrupt-help {
  margin-left: 4px;
  color: #8c8c8c;
  cursor: help;
  font-size: 16px;
}

.vc-interrupt-field {
  margin-bottom: 20px;

  &--nested {
    margin-bottom: 16px;
  }

  &__label {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    font-size: 13px;
    font-weight: 600;
    color: #262626;
    line-height: 20px;
  }
}

.vc-interrupt-protect {
  &__title {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    font-size: 13px;
    font-weight: 600;
    color: #262626;
    line-height: 20px;
  }

  &__box {
    padding: 16px;
    border: 1px solid #d6e4ff;
    border-radius: 8px;
    background: #f7f9ff;
  }
}

.vc-interrupt-field__label--sub {
  font-weight: 500;
}

.vc-interrupt-force-bar {
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px solid #e6ebf5;
}

.vc-interrupt-force-add {
  appearance: none;
  display: inline-flex;
  align-items: center;
  margin: 0;
  padding: 6px 14px;
  border: 1px solid #91b4ff;
  border-radius: 6px;
  background: #fff;
  color: #3d6bff;
  font-size: 13px;
  line-height: 20px;
  cursor: pointer;

  &:hover {
    border-color: #3d6bff;
    background: #f5f8ff;
  }
}

.vc-interrupt-force-collapse {
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #5b7cff;
  font-size: 13px;
  line-height: 20px;
  cursor: pointer;

  &:hover {
    color: #3d6bff;
  }
}
</style>
